import { Banner } from '@customerio/pluma-components/react';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import type { SegmentAlertDescriptor } from '../connection-alert';
import { SegmentConnectionAlert } from './segment-connection-alert';

type ClashNameSource = {
  alert: SegmentAlertDescriptor | null;
  workspaces: readonly { id: number | string; name: string }[];
  credentials: readonly ApiCredential[];
};

/**
 * Who already holds the clashing Segment source.
 *
 * The alert says *that* something holds it; only the page can say *what*, because
 * the API answers with an id. Either the workspace or the credential is named,
 * never both — the error carries one or the other.
 */
function resolveClashNames({ alert, workspaces, credentials }: ClashNameSource) {
  if (alert?.kind !== 'source-already-connected') return { workspaceName: null, credentialName: null };

  return {
    workspaceName:
      alert.otherWorkspaceId == null
        ? null
        : (workspaces.find((candidate) => String(candidate.id) === alert.otherWorkspaceId)?.name ?? null),
    credentialName:
      alert.otherSiteId == null
        ? null
        : (credentials.find((candidate) => candidate.site_id === alert.otherSiteId)?.name ?? null),
  };
}

type SegmentInBannersProps = ClashNameSource & {
  /** Clearing fails on its own terms, so it reports separately from an attempt. */
  clearError: string | null;
  retryLabel: string;
  onDismissAlert: () => void;
  onDismissClearError: () => void;
};

/** Everything above the panel: the attempt's banner, and the clear failure. */
export function SegmentInBanners({
  alert,
  workspaces,
  credentials,
  clearError,
  retryLabel,
  onDismissAlert,
  onDismissClearError,
}: SegmentInBannersProps) {
  const { workspaceName, credentialName } = resolveClashNames({ alert, workspaces, credentials });

  return (
    <>
      {clearError == null ? null : (
        <Banner
          data-test-segment-in-clear-error
          variant="critical"
          icon="critical"
          onClose={onDismissClearError}
          mb="300"
        >
          {clearError}
        </Banner>
      )}

      <SegmentConnectionAlert
        alert={alert}
        otherWorkspaceName={workspaceName}
        otherCredentialName={credentialName}
        retryLabel={retryLabel}
        onClose={onDismissAlert}
      />
    </>
  );
}
